/**
 * Utility functions for Redux thunks
 */

import { createAsyncThunk } from '@reduxjs/toolkit';

// Global registry to track pending requests
const pendingRequestsRegistry = {};
// Global registry for active AbortControllers
const activeAbortControllers = {};

/**
 * Creates a request key based on thunk name and parameters
 * 
 * @param {string} thunkPrefix - The prefix for the thunk action type
 * @param {any} params - The parameters passed to the thunk
 * @returns {string} A unique key for the request
 */
export const createRequestKey = (thunkPrefix, params) => {
  return `${thunkPrefix}:${JSON.stringify(params)}`;
};

/**
 * Creates a condition function that prevents duplicate thunk calls with the same parameters
 * 
 * @param {string} thunkPrefix - The prefix for the thunk action type
 * @returns {Function} A condition function to be used with createAsyncThunk
 */
export const createDuplicateRequestFilter = (thunkPrefix) => {
  return (params, { getState }) => {
    const requestKey = createRequestKey(thunkPrefix, params);
    
    // If there's already a pending request with these exact parameters, skip this one
    if (pendingRequestsRegistry[requestKey]) {
      return false;
    }
    
    // Mark this request as pending
    pendingRequestsRegistry[requestKey] = true;
    
    return true;
  };
};

/**
 * Clears the pending flag for a request when it completes
 * 
 * @param {string} thunkPrefix - The prefix for the thunk action type
 * @param {any} params - The parameters that were passed to the thunk
 */
export const clearPendingRequest = (thunkPrefix, params) => {
  const requestKey = createRequestKey(thunkPrefix, params);
  delete pendingRequestsRegistry[requestKey];
  // Also remove the AbortController
  if (activeAbortControllers[requestKey]) {
    delete activeAbortControllers[requestKey];
  }
};

/**
 * Cancels all pending requests, with an option to exclude specific requests.
 * @param {object} options - Options for cancellation.
 * @param {string[]} options.exceptRequestKeys - Array of request keys to not cancel.
 */
export const cancelAllPendingRequests = (options = {}) => {
  const { exceptRequestKeys = [] } = options;
  for (const requestKey in activeAbortControllers) {
    if (!exceptRequestKeys.includes(requestKey)) {
      activeAbortControllers[requestKey].abort();
      delete activeAbortControllers[requestKey];
      // Also clean up from pendingRequestsRegistry if it exists there
      // This assumes requestKey format is consistent
      const thunkPrefix = requestKey.substring(0, requestKey.indexOf(':'));
      const paramsString = requestKey.substring(requestKey.indexOf(':') + 1);
      try {
        const params = JSON.parse(paramsString);
        delete pendingRequestsRegistry[createRequestKey(thunkPrefix, params)];
      } catch (e) {
        // console.error('Error parsing params for cleanup in cancelAllPendingRequests:', e);
      }
    }
  }
};

/**
 * Creates an enhanced async thunk that automatically prevents duplicate requests
 * This is a wrapper around createAsyncThunk that adds deduplication functionality
 * 
 * @param {string} typePrefix - The action type prefix
 * @param {Function} payloadCreator - The async function that returns the payload
 * @param {object} options - Additional options for createAsyncThunk
 * @returns {AsyncThunk} A Redux async thunk action creator
 */
export const createDedupedAsyncThunk = (typePrefix, payloadCreator, options = {}) => {
  const enhancedOptions = {
    ...options,
    condition: options.condition 
      ? (arg, api) => {
          // First check our duplicate filter, then the user's condition if provided
          const notDuplicate = createDuplicateRequestFilter(typePrefix)(arg, api);
          return notDuplicate ? (options.condition(arg, api) ?? true) : false;
        }
      : createDuplicateRequestFilter(typePrefix)
  };

  // Wrap the original payloadCreator to manage AbortController
  const wrappedPayloadCreator = async (arg, thunkAPI) => {
    const requestKey = createRequestKey(typePrefix, arg);
    const controller = new AbortController();
    activeAbortControllers[requestKey] = controller;

    // Add the signal to thunkAPI if not already there by default (it should be)
    const extendedThunkAPI = { ...thunkAPI, signal: controller.signal };

    try {
      // Pass the extendedThunkAPI (which includes the signal) to the original payloadCreator
      return await payloadCreator(arg, extendedThunkAPI);
    } catch (error) {
      if (error.name === 'AbortError') {
        // Rethrow the error so Redux Toolkit can handle it as a rejected thunk
        throw error;
      }
      // For other errors, also rethrow them
      throw error;
    } finally {
      // Clean up the AbortController if the request wasn't cancelled by cancelAllPendingRequests
      if (activeAbortControllers[requestKey] === controller) { // ensure it's the same controller
        delete activeAbortControllers[requestKey];
      } else {
        // If the controller was replaced, it means the request was cancelled
        // so we don't need to clean up here
      }
    }
  };

  const thunk = createAsyncThunk(typePrefix, wrappedPayloadCreator, enhancedOptions);

  return thunk;
};
